import { useQuery, useZero } from "@rocicorp/zero/react";
import { glob, keyframes, styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { latestWorkflowSuggestion } from "../../../shared/workflowSuggestion";
import { useChatSounds } from "../../hooks/useChatSounds";
import { useSpaces } from "../../hooks/useSpaces";
import { clientSendContext } from "../../lib/clientSendContext";
import { playPop } from "../../lib/sounds";
import { navigate, routes } from "../../lib/urls";
import { CircleMesh } from "../CircleMesh";
import { parseBadgeConfig } from "../CircleMeshEditor";
import { ClickButton } from "../ClickButton";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";

function usePersonalSpaceId(): string | null {
  const [personalSpace] = useQuery(queries.personalSpace());
  return personalSpace?.id ?? null;
}

const TYPEWRITER_SPEED = 60;
const SEEN_STORAGE_PREFIX = "derive-button-seen:";

function isFirstSeen(key: string | null): boolean {
  if (!key || typeof localStorage === "undefined") return false;
  return localStorage.getItem(`${SEEN_STORAGE_PREFIX}${key}`) !== "1";
}

function markSeen(key: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(`${SEEN_STORAGE_PREFIX}${key}`, "1");
}

glob`
  @property --derive-button-gradient-angle {
    syntax: "<angle>";
    initial-value: 90deg;
    inherits: false;
  }
`;

const sweep = keyframes`
  to { --derive-button-gradient-angle: 450deg; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.6); }
  60% { transform: scale(1.08); }
  to { opacity: 1; transform: scale(1); }
`;

const blink = keyframes`
  0%, 30% { opacity: 1; }
  50%, 80% { opacity: 0; }
  100% { opacity: 1; }
`;

const FloatShell = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  box-sizing: border-box;
  transform-origin: center;
  &[data-building="true"] {
    outline: none;
  }
  &[data-animate-in="true"] {
    animation: ${popIn} 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
`;

const baseButtonStyles = `
  position: relative;
  display: flex;
  align-items: center;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--alpha4);
  }
  &:disabled {
    cursor: default;
    opacity: 0.4;
    background: transparent;
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const IconButton = styled(ClickButton)`
  ${baseButtonStyles}
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
`;

const PillButton = styled(ClickButton)`
  ${baseButtonStyles}
  gap: 6px;
  padding: 0 10px 0 8px;
  height: 24px;
`;

const BuildingOutline = styled("span")`
  position: absolute;
  inset: -1px;
  border-radius: 9999px;
  pointer-events: none;
  padding: 1px;
  background: conic-gradient(
    from var(--derive-button-gradient-angle),
    var(--pink-600) 0deg 180deg,
    var(--sky-600) 180deg 270deg,
    var(--orange-600) 270deg 313.2deg,
    var(--teal-600) 313.2deg 360deg
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  animation: ${sweep} 5s linear infinite;
`;

const NeedsYouOutline = styled("span")`
  position: absolute;
  inset: -1px;
  border-radius: 9999px;
  pointer-events: none;
  padding: 1px;
  background: var(--orange-600);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  animation: ${blink} 0.8s ease-in-out infinite;
`;

const IconSlot = styled("span")`
  display: inline-flex;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  overflow: hidden;
`;

type Props = {
  personalChatId: string;
  chatOwnerId: string;
};

export function DeriveWorkflowButton({ personalChatId, chatOwnerId }: Props) {
  const z = useZero();
  const personalSpaceId = usePersonalSpaceId();
  const [personalChat] = useQuery(
    queries.loadPersonalChat({ personalChatId, userId: chatOwnerId })
  );
  const [preferences] = useQuery(queries.userPreferences());
  const [spaces] = useSpaces({ settingsView: false });

  const workflow = personalChat?.derivedWorkflow;
  const suggestion = latestWorkflowSuggestion(
    personalChat?.chat?.entries ?? []
  );
  const branch = workflow?.branches?.[0];
  const branchChat = branch?.headCommitChat;
  const isResponding = branchChat?.respondingAt != null;
  const needsYou = branchChat?.awaitingAction === true && !isResponding;
  useChatSounds(
    needsYou ? "waiting" : isResponding ? "building" : "resting",
    false,
    workflow != null
  );

  const navigateToWorkflow = () => {
    if (!workflow || !branch) return;
    navigate(routes.space.workflow, {
      spaceId: workflow.spaceId,
      workflowId: workflow.id,
      branchId: branch.id,
    });
  };

  const startDerivation = () => {
    const preferredId = preferences?.defaultSpace ?? null;
    const inSpaces = preferredId
      ? spaces.find((s) => s.id === preferredId)?.id
      : null;
    const spaceId = inSpaces ?? personalSpaceId;
    if (!spaceId) return;
    z.mutate(
      sharedMutators.deriveWorkflowFromChat({
        personalChatId,
        workflowId: nanoid(12),
        branchId: nanoid(12),
        commitId: nanoid(12),
        chatId: nanoid(12),
        userEntryId: nanoid(12),
        spaceId,
        ...clientSendContext(),
      })
    );
  };

  const handleClick = workflow ? navigateToWorkflow : startDerivation;

  const namedWorkflow =
    workflow?.name && workflow.name !== "Untitled" ? workflow.name : null;

  const badgeSeenKey = workflow ? `badge-${workflow.id}` : null;
  const nameSeenKey = workflow ? `name-${workflow.id}` : null;
  const appearSeenKey = `appear-${personalChatId}`;
  // Memoise so the animate flag stays true throughout the component's lifetime
  // for this key — otherwise a parent re-render after markSeen flips it back
  // to false and halts mid-animation (e.g. the CircleMesh shuffle).
  const animateBadge = useMemo(() => isFirstSeen(badgeSeenKey), [badgeSeenKey]);
  const animateName = useMemo(() => isFirstSeen(nameSeenKey), [nameSeenKey]);
  const buttonShown = workflow != null || suggestion != null;
  const animateAppear = useMemo(
    () => buttonShown && isFirstSeen(appearSeenKey),
    [buttonShown, appearSeenKey]
  );

  useEffect(() => {
    if (badgeSeenKey && animateBadge) markSeen(badgeSeenKey);
  }, [badgeSeenKey, animateBadge]);
  useEffect(() => {
    if (nameSeenKey && namedWorkflow && animateName) markSeen(nameSeenKey);
  }, [nameSeenKey, namedWorkflow, animateName]);
  useEffect(() => {
    if (animateAppear) {
      markSeen(appearSeenKey);
      playPop();
    }
  }, [animateAppear, appearSeenKey]);

  const iconBadge = workflow ? (
    <CircleMesh
      key={`derived-${workflow.id}`}
      targetCircleSize={6}
      seed={parseBadgeConfig(workflow.badgeSeed, workflow.id).seed}
      restrictedColors={
        parseBadgeConfig(workflow.badgeSeed, workflow.id).colors
      }
      mutingIntensity={0}
      animate={animateBadge}
      grayscale={workflow?.pausedAt != null}
    />
  ) : (
    <Icon name="3b-mark" size={12} />
  );

  if (!buttonShown) return null;

  const tooltipContent = needsYou
    ? "3B needs you"
    : isResponding
      ? "3B is building"
      : !workflow
        ? "Turn into workflow"
        : null;

  if (!namedWorkflow) {
    return (
      <FloatShell
        data-building={isResponding}
        data-needs-you={needsYou}
        data-animate-in={animateAppear}
      >
        <Tooltip
          content={tooltipContent}
          align="center"
          hidden={!tooltipContent}
        >
          <IconButton
            type="button"
            aria-label={workflow ? "Open workflow" : "Turn into workflow"}
            onClick={handleClick}
          >
            <IconSlot>{iconBadge}</IconSlot>
          </IconButton>
        </Tooltip>
        {needsYou ? (
          <NeedsYouOutline />
        ) : isResponding ? (
          <BuildingOutline />
        ) : null}
      </FloatShell>
    );
  }

  return (
    <FloatShell
      data-building={isResponding}
      data-needs-you={needsYou}
      data-animate-in={animateAppear}
    >
      <Tooltip content={tooltipContent} align="center" hidden={!tooltipContent}>
        <PillButton type="button" onClick={handleClick}>
          <IconSlot>{iconBadge}</IconSlot>
          <Text variant="text-small-md" color="var(--text-primary)" ellipsis>
            <TypewriterFromEmpty text={namedWorkflow} animate={animateName} />
          </Text>
        </PillButton>
      </Tooltip>
      {needsYou ? (
        <NeedsYouOutline />
      ) : isResponding ? (
        <BuildingOutline />
      ) : null}
    </FloatShell>
  );
}

function TypewriterFromEmpty({
  text,
  animate,
}: {
  text: string;
  animate: boolean;
}) {
  const speed = TYPEWRITER_SPEED;
  const [displayed, setDisplayed] = useState(animate ? "" : text);

  useEffect(() => {
    if (displayed === text) return;
    const timer = setTimeout(() => {
      const isPrefix = text.startsWith(displayed);
      if (isPrefix && displayed.length < text.length) {
        setDisplayed(text.slice(0, displayed.length + 1));
      } else if (displayed.length > 0) {
        setDisplayed(displayed.slice(0, -1));
      }
    }, 1000 / speed);
    return () => clearTimeout(timer);
  }, [text, displayed, speed]);

  return displayed;
}
